import { useState, useCallback } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  PanelFooter,
  Stack,
  Text,
  Flex,
  Label,
  Link,
  Spinner,
  FormattedRelativeTime,
} from '@customerio/pluma-components/react';
import type { SegmentNode, ConditionNode as ConditionNodeType } from '../../types';
import { parseConditions } from '../../parse';
import { ConditionCard } from '../condition-card';
import { Bold } from '../condition-parts';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import * as styles from './segment.css';

function useSegmentDetails(segmentId: number, workspaceId: number, enabled: boolean) {
  const segmentQuery = useHydraUIAPIQuery({
    queryKey: ['getSegmentById', { params: { environment_id: workspaceId, segment_id: segmentId } }],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.get({
        params: { path: { environment_id: String(workspaceId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: enabled && workspaceId > 0 && segmentId > 0, staleTime: 5 * 60 * 1000 },
  });

  const countQuery = useHydraUIAPIQuery({
    queryKey: ['getSegmentCount', { params: { environment_id: workspaceId, segment_id: segmentId } }],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.getCount({
        params: { path: { environment_id: String(workspaceId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: enabled && workspaceId > 0 && segmentId > 0, staleTime: 5 * 60 * 1000 },
  });

  return {
    segment: segmentQuery.data?.segment,
    count: countQuery.data?.count,
    isLoading: segmentQuery.isLoading,
    error: segmentQuery.error,
  };
}

function formatPeopleCount(count: number | undefined): string | null {
  if (count == null) return null;
  if (count === 1) return '1 person';
  return `${count.toLocaleString()} people`;
}

type RenderCondition = (props: { node: ConditionNodeType; depth?: number; workspaceId: number }) => React.ReactNode;

interface SegmentPopoverProps {
  segment: NonNullable<ReturnType<typeof useSegmentDetails>['segment']>;
  count: number | undefined;
  workspaceId: number;
  renderCondition: RenderCondition;
}

function SegmentPopoverContent({ segment, count, workspaceId, renderCondition }: SegmentPopoverProps) {
  // The generated segment `type` enum omits the legacy `manual` alias (now surfaced as `static`);
  // widen to preserve the historical manual-segment branch in case the API still returns it.
  const isManual = (segment.type as string) === 'manual' || segment.type === 'static';
  const conditions = segment.conditions;
  const peopleCount = formatPeopleCount(count);
  const segmentUrl = `/env/${workspaceId}/segments/${segment.id}`;

  return (
    <PopoverContent p="0" className={styles.popoverMinWidth}>
      <Panel withSectionBorders className={styles.popoverPanel}>
        <PanelHeader py="150" px="300">
          <Stack gap="025">
            <Text size="xs" color="subtle">
              Segment {segment.id}
            </Text>
            <Flex gap="100" alignItems="center">
              <PanelTitle level="5" size="m">
                {segment.name}
              </PanelTitle>
              {segment.archived === true ? <Label color="red">Archived</Label> : null}
            </Flex>
          </Stack>
        </PanelHeader>

        <PanelSection pt="200" pb="150" px="300" className={styles.popoverConditions}>
          <Text size="xs" color="subtle" mb="050" display="inline-block">
            {isManual ? 'Manual Segment' : 'Data-driven Segment'}
          </Text>

          {isManual ? (
            <Text size="s">
              <Text as="span" weight="semibold">
                Created:
              </Text>{' '}
              <FormattedRelativeTime date={segment.created_at} withTooltip />
            </Text>
          ) : conditions != null && typeof conditions === 'object' ? (
            renderCondition({ node: parseConditions(conditions), workspaceId })
          ) : (
            <Text size="s" color="subtle">
              No conditions defined
            </Text>
          )}
        </PanelSection>

        <PanelFooter py="150" px="300">
          <Flex justifyContent="space-between" alignItems="center" w="full">
            <Link href={segmentUrl} variant="secondary" isUnderlined target="_blank">
              View segment
            </Link>
            {peopleCount != null ? (
              <Text size="xs" color="subtle">
                {peopleCount}
              </Text>
            ) : null}
          </Flex>
        </PanelFooter>
      </Panel>
    </PopoverContent>
  );
}

interface SegmentConditionProps {
  node: SegmentNode;
  workspaceId: number;
  renderCondition: RenderCondition;
}

export function SegmentCondition({ node, workspaceId, renderCondition }: SegmentConditionProps) {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { segment, count, isLoading, error } = useSegmentDetails(node.segmentId, workspaceId, shouldFetch);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !shouldFetch) {
        setShouldFetch(true);
      }
    },
    [shouldFetch],
  );

  const name = node.segmentName ?? segment?.name ?? `segment #${node.segmentId}`;

  return (
    <ConditionCard node={node}>
      {node.inverse ? 'Not in' : 'In'}{' '}
      <Popover shouldTriggerOnClick={false} shouldTriggerOnHover placement="bottom" onOpenChange={handleOpenChange}>
        <PopoverTrigger cursor="help" display="inline-flex" alignItems="center">
          <Bold>{name}</Bold>
        </PopoverTrigger>
        {isLoading ? (
          <PopoverContent p="300">
            <Flex gap="100" alignItems="center" justifyContent="center">
              <Spinner />
              <Text size="s" color="subtle">
                Loading segment…
              </Text>
            </Flex>
          </PopoverContent>
        ) : segment != null ? (
          <SegmentPopoverContent
            segment={segment}
            count={count}
            workspaceId={workspaceId}
            renderCondition={renderCondition}
          />
        ) : shouldFetch ? (
          <PopoverContent p="300">
            <Text size="s" color="critical">
              {error != null ? 'Failed to load segment' : 'Segment not found'}
            </Text>
          </PopoverContent>
        ) : null}
      </Popover>{' '}
      segment
    </ConditionCard>
  );
}
